import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { http } from '@lib/api';
import { validatePassword } from '@lib/password';
import { BrandAuthShell } from '@components/brand/BrandAuthShell';
import { BrandCard } from '@components/brand/BrandCard';
import PasswordField from '@components/ui/PasswordField';
import PasswordStrengthMeter from '@components/ui/PasswordStrengthMeter';

/**
 * Invitation accept page (#1652) — the landing target of the emailed
 * `INVITE_ACCEPT_URL?token=…` link. Public route: the invitee has no
 * session yet, and the one-time token in the URL is the only credential.
 * Registration stays invitation-only, so this is the sole path from a
 * PENDING account to an active one.
 */
type Status = 'idle' | 'submitting' | 'success' | 'error' | 'invalid';

const AcceptInvitation: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>(token ? 'idle' : 'invalid');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      newErrors.password = passwordValidation.message || t('passwordChange.passwordInvalid');
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.accept.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !validate() || status === 'submitting') return;

    setStatus('submitting');
    setApiError('');

    try {
      await http.post('/api/invitations/accept', { token, password });
      setStatus('success');
    } catch (err: unknown) {
      // The backend answers with one opaque 4xx for unknown / expired /
      // already-used tokens (no enumeration oracle), so the server message
      // is shown as-is rather than being mapped to a reason here.
      setStatus('error');
      setApiError(
        (err instanceof Error ? err.message : undefined) || t('auth.accept.failedMessage'),
      );
    }
  };

  if (status === 'invalid') {
    return (
      <BrandAuthShell>
        <BrandCard title={t('auth.accept.invalidLink')}>
          <div role="alert" className="fc-stack">
            <p className="fc-card-lead">{t('auth.accept.invalidLinkMessage')}</p>
            <Link to="/login">{t('auth.accept.backToLogin')}</Link>
          </div>
        </BrandCard>
      </BrandAuthShell>
    );
  }

  if (status === 'success') {
    return (
      <BrandAuthShell>
        <BrandCard title={t('auth.accept.success')}>
          <div role="status" className="fc-stack">
            <p className="fc-card-lead">{t('auth.accept.successMessage')}</p>
            <button type="button" className="fc-button" onClick={() => navigate('/login')}>
              {t('auth.accept.goToLogin')}
            </button>
          </div>
        </BrandCard>
      </BrandAuthShell>
    );
  }

  return (
    <BrandAuthShell>
      <BrandCard title={t('auth.accept.title')}>
        <form className="fc-stack" onSubmit={handleSubmit}>
          <p className="fc-card-lead">{t('auth.accept.subtitle')}</p>

          {apiError && (
            <div role="alert" className="fc-alert">
              {apiError}
            </div>
          )}

          <PasswordField
            name="password"
            label={t('auth.accept.password')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
            }}
            error={errors.password}
            autoComplete="new-password"
            required
          />

          {password && <PasswordStrengthMeter password={password} />}

          <PasswordField
            name="confirmPassword"
            label={t('auth.accept.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (errors.confirmPassword) {
                setErrors((prev) => ({ ...prev, confirmPassword: '' }));
              }
            }}
            error={errors.confirmPassword}
            autoComplete="new-password"
            required
          />

          <button type="submit" className="fc-button" disabled={status === 'submitting'}>
            {status === 'submitting' ? t('auth.accept.submitting') : t('auth.accept.submit')}
          </button>

          <Link to="/login">{t('auth.accept.backToLogin')}</Link>
        </form>
      </BrandCard>
    </BrandAuthShell>
  );
};

export default AcceptInvitation;
